import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
// Imported directly rather than through the atoms barrel: that barrel pulls in
// CountrySelect and the router, which this leaf component has no use for.
import Copy from 'components/components/atoms/Copy';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import useHasMounted from 'utils/hooks/useHasMounted';
import {
  getPlainText,
  getMaxCharacterCount,
  truncateHTMLContent,
} from 'utils/truncateRichText';

// Rich text that shows a preview with a More+ / Less- toggle once it runs past
// the breakpoint's character budget, matching the Community product overview.
// Copy long enough to need this is a wall of text on a phone (MTTMY-2189).
const ExpandableCopy = ({ text = '', className = '', color = 'blue' }) => {
  const hasMounted = useHasMounted();
  const breakpoint = useBreakpoint();
  const maxCharacterCount = getMaxCharacterCount(breakpoint);

  const [open, setOpen] = useState(false);

  // Derived without the DOM so the server produces the same value as the
  // client; useBreakpoint() reports the SSR breakpoint on the first render on
  // both sides, so this branch agrees across hydration (React #418).
  const plainText = getPlainText(text);

  if (plainText.length <= maxCharacterCount) {
    return (
      <Copy className={className} color={color}>
        {text}
      </Copy>
    );
  }

  // truncateHTMLContent() walks the DOM, so it cannot run on the server. Use
  // the plain-text truncation for the first render on BOTH sides, then upgrade
  // to the markup-preserving version once mounted.
  const preview = hasMounted
    ? truncateHTMLContent(text, maxCharacterCount - 3)
    : plainText.substring(0, maxCharacterCount - 3) + '...';

  // The full copy is always rendered (just hidden while collapsed) so it is in
  // the server-rendered HTML for crawlers, not only after More+ is pressed.
  return (
    <>
      {!open && (
        <Copy className={className} color={color}>
          {preview}
        </Copy>
      )}
      <Copy className={className} color={color} hidden={!open}>
        {text}
      </Copy>
      <button
        type='button'
        className={cx('font-graphie font-bold cursor-pointer text-left mt-1', {
          'text-black': color === 'black',
          'text-mattamy-blue': color !== 'black',
        })}
        aria-label={open ? 'Show less' : 'Show more'}
        aria-expanded={open}
        onClick={(e) => {
          // Matches the CTA button in ExpansionPanel: this renders inside an
          // accordion, so keep the click from reaching any enclosing handler.
          e.stopPropagation();
          setOpen(!open);
        }}
      >
        {open ? 'Less-' : 'More+'}
      </button>
    </>
  );
};

ExpandableCopy.propTypes = {
  text: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
};

export default ExpandableCopy;
